import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMaintenanceMode } from '@/contexts/MaintenanceContext';
import { useToast } from '@/hooks/use-sonner-toast';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, isAuthenticated, loading } = useAuth();
  const { canAccessForge, maintenanceMessage } = useMaintenanceMode();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Show toast if user is logged in but not authenticated (email not verified)
    if (currentUser && !isAuthenticated && !loading) {
      toast({
        title: 'Email verification required',
        description: 'Please verify your email address to access this page.',
        variant: 'destructive',
      });
    }

    // Show toast if maintenance mode is active and user can't access forge
    if (currentUser && isAuthenticated && !canAccessForge) {
      toast({
        title: 'Forge Access Restricted',
        description: maintenanceMessage,
        variant: 'destructive',
      });
    }
  }, [currentUser, isAuthenticated, loading, canAccessForge, maintenanceMessage, toast]);

  // Show loading state while checking authentication
  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // If not logged in, redirect to landing page
  if (!currentUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If logged in but not authenticated (email not verified), redirect to landing page
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If maintenance mode is active and user can't access forge, redirect to landing page
  if (!canAccessForge) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // If authenticated and can access forge, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
